// client/src/components/messages/ThreadList.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../../axiosInstance';
import { io } from 'socket.io-client';
import CreateGroupDialog from './CreateGroupDialog';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemButton,
  CircularProgress, 
  Alert,
  Chip,
  Paper,
  Button,
  Fab,
  Avatar,
  ListItemAvatar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import AppBarHeader from '../AppBarHeader';

const ThreadList = () => {
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!isFeatureEnabled('enableMessageBoardFeature')) {
      navigate('/dashboard');
      return;
    }
    fetchThreads();
  }, [isFeatureEnabled, navigate, showArchived]);

  // Setup Socket.IO for real-time unread count updates (Phase 3)
  useEffect(() => {
    if (!token || !userId) return;

    const socket = io(window.location.origin, {
      path: '/socket.io/',
      auth: { token },
      transports: ['polling'],
      secure: true,
      rejectUnauthorized: false,
      upgrade: false
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ ThreadList: Socket.IO connected for unread updates');
    });

    socket.on('unread-count-update', ({ threadId, unreadCount }) => {
      console.log('📬 Unread count update:', { threadId, unreadCount });
      setThreads(prevThreads => 
        prevThreads.map(thread => 
          String(thread._id) === String(threadId)
            ? { ...thread, unreadCount }
            : thread
        )
      );
    });

    socket.on('connect_error', (err) => {
      console.error('❌ ThreadList Socket.IO error:', err.message);
    });

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [token, userId]);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      // When showArchived is true, we want ONLY archived threads
      // When showArchived is false, we want ONLY active (non-archived) threads
      const params = showArchived 
        ? { includeArchived: 'true', archivedOnly: 'true' }
        : { includeArchived: 'false' };
      
      const response = await axiosInstance.get('/messages/threads', { params });
      setThreads(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching threads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveToggle = async (threadId, currentStatus) => {
    try {
      await axiosInstance.patch(`/messages/threads/${threadId}`, {
        isArchived: !currentStatus
      });
      fetchThreads();
    } catch (err) {
      console.error('Error toggling archive:', err);
      alert(err.response?.data?.error || 'Failed to update thread');
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this thread? This cannot be undone.')) return;

    try {
      await axiosInstance.delete(`/messages/threads/${threadId}`);
      fetchThreads();
    } catch (err) {
      console.error('Error deleting thread:', err);
      alert(err.response?.data?.error || 'Failed to delete thread');
    }
  };

  const getOtherParticipant = (thread) => {
    if (!thread.participants || !userId) return null;
    return thread.participants.find(p => String(p._id) !== String(userId));
  };

  const getParticipantNames = (thread) => {
    if (!thread.participants || !userId) return 'Unknown';
    const others = thread.participants.filter(p => String(p._id) !== String(userId));
    if (others.length === 0) return 'You';
    return others.map(p => p.username).join(', ');
  };

  const handleGroupCreated = (group) => {
    navigate(`/messages/thread/${group._id}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleThreadClick = (threadId) => {
    navigate(`/messages/thread/${threadId}`);
  };

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} hideCollectionNav={true} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} hideCollectionNav={true} />
      <Box sx={{ maxWidth: 800, margin: '0 auto', p: 2, position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            Messages
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setGroupDialogOpen(true)}
            >
              New Group
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/community')}
            >
              New Conversation
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={showArchived ? 'archived' : 'active'}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setShowArchived(newValue === 'archived');
              }
            }}
            aria-label="thread filter"
          >
            <ToggleButton value="active" aria-label="active threads">
              Active
            </ToggleButton>
            <ToggleButton value="archived" aria-label="archived threads">
              Archived
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {threads.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            No conversations yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visit the Community page and click "Message" on any user to start a conversation.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/community')}
            sx={{ mt: 2 }}
          >
            Go to Community
          </Button>
        </Paper>
      ) : (
        <List>
          {threads.map((thread) => {
            const otherParticipant = getOtherParticipant(thread);
            const participantNames = getParticipantNames(thread);
            
            return (
              <ListItem key={thread._id} disablePadding>
                <ListItemButton onClick={() => handleThreadClick(thread._id)}>
                  <ListItemAvatar>
                    <Avatar
                      src={otherParticipant?.profileImage ? axiosInstance.getImageUrl(otherParticipant.profileImage) : null}
                      alt={otherParticipant?.username || 'User'}
                    >
                      {otherParticipant?.username?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            sx={{ fontWeight: thread.unreadCount > 0 ? 'bold' : 'normal' }}
                          >
                            {thread.title || 'Untitled Conversation'}
                          </Typography>
                          {thread.isGroup && (
                            <Chip label="Group" size="small" color="primary" />
                          )}
                          {thread.unreadCount > 0 && (
                            <Chip 
                              label={thread.unreadCount} 
                              color="error" 
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                        </Box>
                        <Typography 
                          variant="body2" 
                          component="div" 
                          color="text.secondary"
                        >
                          {thread.isGroup ? (
                            `${thread.participants.length} participants`
                          ) : (
                            <>
                              with{' '}
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ 
                                  cursor: 'pointer', 
                                  '&:hover': { textDecoration: 'underline' },
                                  display: 'inline'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (otherParticipant) navigate(`/collections/${otherParticipant._id}`);
                                }}
                              >
                                {participantNames}
                              </Typography>
                            </>
                          )}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {thread.categoryId && (
                          <Chip 
                            label={thread.categoryId.name} 
                            size="small" 
                            sx={{ 
                              backgroundColor: thread.categoryId.color,
                              color: '#fff'
                            }}
                          />
                        )}
                        <Typography variant="body2" component="span">
                          {thread.messageCount} message{thread.messageCount !== 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="body2" component="span">
                          •
                        </Typography>
                        <Typography variant="body2" component="span">
                          {new Date(thread.lastMessageAt).toLocaleDateString()}
                        </Typography>
                        {thread.isLocked && (
                          <Chip 
                            label="Locked" 
                            size="small" 
                            color="warning" 
                          />
                        )}
                        {thread.isArchived && (
                          <Chip 
                            label="Archived" 
                            size="small" 
                            color="default" 
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
      </Box>

      <CreateGroupDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        onGroupCreated={handleGroupCreated}
      />
    </>
  );
};

export default ThreadList;
